import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { collegeJourneyService } from '../../lib/college-journey-service';
import { 
  ArrowLeft, 
  ArrowRight, 
  Save, 
  Users, 
  User,
  MessageCircle,
  CheckCircle,
  Eye,
  Heart, 
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown
} from 'lucide-react';

// Parent B Questions - Enhanced with additional perspective questions
const parentBQuestions = [
  {
    id: 'easy_subject',
    question: "What subject or activity does your child find surprisingly easy that others struggle with?",
    placeholder: "Think about natural strengths that seem effortless for them...",
    category: 'Academic & Learning Profile',
    section: 'Academic & Learning Profile'
  },
  {
    id: 'growth',
    question: "Describe a time when you watched your child grow through a challenge or setback.",
    placeholder: "Focus on resilience, problem-solving, or character development...",
    category: 'Academic & Learning Profile',
    section: 'Academic & Learning Profile'
  },
  {
    id: 'learning_difference',
    question: "When has your child's way of thinking or learning been different from what schools typically expect?",
    placeholder: "Times when their approach didn't match standard methods but still worked...",
    category: 'System Interactions & Edge Cases',
    section: 'System Interactions & Edge Cases'
  },
  {
    id: 'surprises',
    question: "What does your child do that consistently surprises adults (teachers, coaches, family friends)?",
    placeholder: "Behaviors, insights, or abilities that catch adults off guard...",
    category: 'System Interactions & Edge Cases',
    section: 'System Interactions & Edge Cases'
  },
  {
    id: 'age_limit',
    question: "Has your child ever been told they couldn't do something due to age/grade level, but you suspected they could handle it?",
    placeholder: "Times when age restrictions seemed arbitrary given their capabilities...",
    category: 'System Interactions & Edge Cases',
    section: 'System Interactions & Edge Cases'
  },
  {
    id: 'rules_questioned',
    question: "What rules or systems has your child questioned that made you think 'actually, they have a point'?",
    placeholder: "Legitimate challenges to authority or conventional wisdom...",
    category: 'System Interactions & Edge Cases',
    section: 'System Interactions & Edge Cases'
  },
  {
    id: 'wrong_level',
    question: "Has your child ever been placed in the 'wrong' level for something, either too easy or too hard?",
    placeholder: "E.g., Reading at college level but placed in grade-level English, or struggled in advanced math despite being capable...",
    category: 'System Interactions & Edge Cases',
    section: 'System Interactions & Edge Cases'
  },
  {
    id: 'advocacy_success',
    question: "Describe a time your child advocated for themselves or others and succeeded in changing an outcome.",
    placeholder: "Times when they pushed back against decisions and proved they were right...",
    category: 'System Interactions & Edge Cases',
    section: 'System Interactions & Edge Cases'
  },
  {
    id: 'initiative_against_advice',
    question: "When has your child taken initiative on something despite being advised against it? What was the outcome?",
    placeholder: "Times they chose their own path despite concerns from parents/teachers...",
    category: 'Natural Tendencies & Uniqueness',
    section: 'Natural Tendencies & Uniqueness'
  },
  {
    id: 'mechanical_skills',
    question: "What practical skills or mechanical abilities does your child have that surprise you?",
    placeholder: "Building, fixing, troubleshooting, understanding how things work...",
    category: 'Natural Tendencies & Uniqueness',
    section: 'Natural Tendencies & Uniqueness'
  },
  {
    id: 'math_integration',
    question: "When does your child bring mathematical thinking into unexpected situations or conversations?",
    placeholder: "Times they apply math/logic outside of academic contexts...",
    category: 'Natural Tendencies & Uniqueness',
    section: 'Natural Tendencies & Uniqueness'
  },
  {
    id: 'entrepreneurial_instincts',
    question: "When has your child shown entrepreneurial instincts or figured out how to create/optimize value in social or economic situations?",
    placeholder: "Times they identified opportunities, created businesses, optimized trades, or applied strategic thinking to social systems...",
    category: 'Natural Tendencies & Uniqueness',
    section: 'Natural Tendencies & Uniqueness'
  },
  {
    id: 'obsessions',
    question: "What does your child get obsessed with or spend way too much time on?",
    placeholder: "Areas of intense focus or deep interest...",
    category: 'Natural Tendencies & Uniqueness',
    section: 'Natural Tendencies & Uniqueness'
  },
  {
    id: 'unusual_interests',
    question: "What's something your child cares deeply about that doesn't seem typical for their age/peer group?",
    placeholder: "Interests or values that set them apart from peers...",
    category: 'Natural Tendencies & Uniqueness',
    section: 'Natural Tendencies & Uniqueness'
  },
  {
    id: 'hidden_qualities',
    question: "What do you hope college admissions officers will understand about your child that might not show up in grades/scores?",
    placeholder: "Important qualities that traditional metrics don't capture...",
    category: 'Natural Tendencies & Uniqueness',
    section: 'Natural Tendencies & Uniqueness'
  },
  {
    id: 'different_perspective',
    question: "Where do you and your spouse see your child differently?",
    placeholder: "Different opinions about their strengths, challenges, or potential...",
    category: 'Parental Perspectives',
    section: 'Parental Perspectives'
  },
  {
    id: 'what_other_parent_misses',
    question: "What does your spouse miss about your child that you see clearly?",
    placeholder: "Insights or qualities that only you seem to notice...",
    category: 'Parental Perspectives',
    section: 'Parental Perspectives'
  }
];

const ParentObservationsB = () => {
  const navigate = useNavigate();
  const { currentUser, family, familyUsers } = useFamilyContext();
  const { updateMasterStrategy } = useCollegeContext();
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showSaveIndicator, setShowSaveIndicator] = useState(false);
  const [studentProfile, setStudentProfile] = useState(null);
  const [parentACompleted, setParentACompleted] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved');
  const [saveMessage, setSaveMessage] = useState('');
  const [validUserId, setValidUserId] = useState(null);

  // Get the selected student - matches ParentObservations logic
  const getStudent = () => {
    // Try to get the saved student from localStorage first
    const savedStudentId = localStorage.getItem('college-selected-student-id');
    if (savedStudentId) {
      // Look in familyUsers first
      if (familyUsers?.length > 0) {
        const savedStudent = familyUsers.find(user => user.id === savedStudentId);
        if (savedStudent) {
          return {
            ...savedStudent,
            type: 'student',
            displayName: savedStudent.name || savedStudent.displayName,
            auth_user_id: savedStudent.authUserId || savedStudent.auth_user_id,
            
          };
        }
      }
      
      // Fallback to family.kids
      if (family?.kids?.length > 0) {
        const savedStudent = family.kids.find(kid => kid.id === savedStudentId);
        if (savedStudent) {
          return {
            ...savedStudent,
            type: 'student',
            displayName: savedStudent.name || savedStudent.displayName,
            auth_user_id: savedStudent.authUserId || savedStudent.auth_user_id,
            
          };
        }
      }
    }
    
    // Default to first child from familyUsers or family.kids
    if (familyUsers?.length > 0) {
      return familyUsers.find(user => user.type !== 'parent');
    }
    
    // Fallback to family.kids
    if (family?.kids?.length > 0) {
      const firstKid = family.kids[0];
      return {
        ...firstKid,
        type: 'student',
        displayName: firstKid.name || firstKid.displayName,
        auth_user_id: firstKid.authUserId || firstKid.auth_user_id,
        
      };
    }
    
    return null;
  };
  
  const student = getStudent();
  
  // Save selected student to localStorage for consistency
  useEffect(() => {
    if (student?.id) {
      localStorage.setItem('college-selected-student-id', student.id);
    }
  }, [student?.id]);

  useEffect(() => {
    if (student) {
      loadExistingData();
    }
  }, [student?.id]); // Only run when student ID changes, not the whole object

  useEffect(() => {
    // Auto-save whenever responses change
    const saveTimeout = setTimeout(() => {
      handleSave();
    }, 2000);
    
    return () => clearTimeout(saveTimeout);
  }, [responses]);

  // Additional periodic save every 30 seconds as backup protection
  useEffect(() => {
    if (!student || !Object.values(responses).some(val => val?.trim())) {
      return;
    }

    const interval = setInterval(() => {
      console.log('🔄 ParentObservationsB: Periodic save...');
      handleSave();
    }, 30000); // Save every 30 seconds

    return () => clearInterval(interval);
  }, [student]); // Only re-setup when student changes

  const loadExistingData = async () => {
    if (!student) return;
    
    console.log('🔍 ParentObservationsB: Loading data for student:', student);
    console.log('🔍 ParentObservationsB: Student fields:', Object.keys(student));
    
    try {
      // Try different possible ID field names for the student
      const possibleIds = [
        student.id,
        student.auth_user_id,
        student.authUserId,
        student.user_id,
        student.userId
      ].filter(Boolean);
      
      console.log('🔍 ParentObservationsB: Trying IDs:', possibleIds);
      
      let profileData = null;
      
      // Try each ID until we find data
      for (const userId of possibleIds) {
        console.log('🔍 ParentObservationsB: Searching with user_id:', userId);
        
        const { data, error } = await supabase
          .from('student_academic_profiles')
          .select(`
            parent_b_easy_subject,
            parent_b_growth,
            parent_b_poor_fit,
            parent_b_learning_difference,
            parent_b_surprises,
            parent_b_age_limit,
            parent_b_rules_questioned,
            parent_b_wrong_level,
            parent_b_advocacy_success,
            parent_b_initiative_against_advice,
            parent_b_mechanical_skills,
            parent_b_math_integration,
            parent_b_entrepreneurial_instincts,
            parent_b_obsessions,
            parent_b_unusual_interests,
            parent_b_hidden_qualities,
            parent_b_different_perspective,
            parent_b_what_other_parent_misses,
            parent_observation_easy_subject
          `)
          .eq('user_id', userId);
        
        if (error) {
          console.log('🔍 ParentObservationsB: Error with user_id', userId, ':', error);
          continue;
        }
        
        console.log('🔍 ParentObservationsB: Results for user_id', userId, ':', data);
        
        if (data && data.length > 0) {
          profileData = data[0];
          console.log('✅ ParentObservationsB: Found profile data:', profileData);
          setValidUserId(userId); // Store the user_id that worked
          break;
        }
      }
      
      if (profileData) {
        const existingResponses = {
          easy_subject: profileData.parent_b_easy_subject || '',
          growth: profileData.parent_b_growth || '',
          learning_difference: profileData.parent_b_learning_difference || profileData.parent_b_poor_fit || '',
          surprises: profileData.parent_b_surprises || '',
          age_limit: profileData.parent_b_age_limit || '',
          rules_questioned: profileData.parent_b_rules_questioned || '',
          wrong_level: profileData.parent_b_wrong_level || '',
          advocacy_success: profileData.parent_b_advocacy_success || '',
          initiative_against_advice: profileData.parent_b_initiative_against_advice || '',
          mechanical_skills: profileData.parent_b_mechanical_skills || '',
          math_integration: profileData.parent_b_math_integration || '',
          entrepreneurial_instincts: profileData.parent_b_entrepreneurial_instincts || '',
          obsessions: profileData.parent_b_obsessions || '',
          unusual_interests: profileData.parent_b_unusual_interests || '',
          hidden_qualities: profileData.parent_b_hidden_qualities || '',
          different_perspective: profileData.parent_b_different_perspective || '',
          what_other_parent_misses: profileData.parent_b_what_other_parent_misses || ''
        };

        setResponses(existingResponses);

        // Check if Parent A has completed observations
        const hasParentAData = !!profileData.parent_observation_easy_subject;
        setParentACompleted(hasParentAData);

        console.log('✅ Loaded existing Parent B responses:', existingResponses);
      }
    } catch (error) {
      console.error('Error loading Parent B data:', error);
    }
  };

  const handleSave = async () => {
    if (!student) {
      setSaveStatus('error');
      setSaveMessage('No student selected');
      return;
    }
    
    setSaveStatus('saving');
    setSaveMessage('Saving observations...');
    
    console.log('💾 ParentObservationsB: Starting save for student:', student);
    
    try {
      // Use the validUserId that we found during loading, or fall back to discovery
      let userId = validUserId;
      
      if (!userId) {
        // If we don't have a valid user_id stored, try to find one
        const possibleIds = [
          student.auth_user_id,
          student.authUserId,
          student.user_id,
          student.userId,
          student.id // Try this last since it failed before
        ].filter(Boolean);
        userId = possibleIds[0];
      }
      
      console.log('💾 ParentObservationsB: Using user_id:', userId);
      
      // Use the improved service method that handles existing profiles properly
      await collegeJourneyService.updateAcademicProfile({
        parent_b_easy_subject: responses.easy_subject || null,
        parent_b_growth: responses.growth || null,
        parent_b_learning_difference: responses.learning_difference || null,
        parent_b_surprises: responses.surprises || null,
        parent_b_age_limit: responses.age_limit || null,
        parent_b_rules_questioned: responses.rules_questioned || null,
        parent_b_wrong_level: responses.wrong_level || null,
        parent_b_advocacy_success: responses.advocacy_success || null,
        parent_b_initiative_against_advice: responses.initiative_against_advice || null,
        parent_b_mechanical_skills: responses.mechanical_skills || null,
        parent_b_math_integration: responses.math_integration || null,
        parent_b_entrepreneurial_instincts: responses.entrepreneurial_instincts || null,
        parent_b_obsessions: responses.obsessions || null,
        parent_b_unusual_interests: responses.unusual_interests || null,
        parent_b_hidden_qualities: responses.hidden_qualities || null,
        parent_b_different_perspective: responses.different_perspective || null,
        parent_b_what_other_parent_misses: responses.what_other_parent_misses || null
      }, userId);
      
      setSaveStatus('saved');
      setSaveMessage('Observations saved successfully!');
      setTimeout(() => {
        setSaveStatus('saved');
        setSaveMessage('');
      }, 2000);
      
    } catch (error) {
      console.error('Error saving observations:', error);
      setSaveStatus('error');
      setSaveMessage('Failed to save observations');
      setTimeout(() => {
        setSaveStatus('saved');
        setSaveMessage('');
      }, 3000);
    }
  };

  const updateField = (field, value) => {
    setResponses(prev => ({ ...prev, [field]: value }));
    if (saveStatus === 'saved') {
      setSaveStatus('unsaved');
    }
  };

  if (!student) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <User className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Student Found</h3>
          <p className="text-slate-600 mb-4">Please select a student to complete parent B observations.</p>
          <button
            onClick={() => navigate('/college')}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800"
          >
            Return to College Hub
          </button>
        </div>
      </div>
    );
  }

  const completedFields = Object.values(responses).filter(value => value && value.trim()).length;
  const totalFields = parentBQuestions.length;
  const progressPercentage = Math.round((completedFields / totalFields) * 100);

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/college')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to College Hub
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Parent B Observations</h1>
            <p className="text-lg text-slate-600 mt-2">
              Second parent perspective about <strong>{student.displayName || student.name}</strong>
            </p>
          </div>
          
          <div className="text-right">
            <div className="text-2xl font-bold text-slate-900">{progressPercentage}%</div>
            <div className="text-sm text-slate-600">{completedFields} of {totalFields} complete</div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-slate-200 rounded-full h-2 mt-4">
          <div 
            className="bg-slate-900 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {!parentACompleted && (
        <div className="mb-8">
          <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Eye className="w-5 h-5 text-azure-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-azure-900 mb-2">Second Parent Perspective</h3>
                <p className="text-azure-700 text-sm mb-3">
                  These observations will complement the first parent's perspective to create a more comprehensive 
                  strategic assessment. You can complete these in any order.
                </p>
                <button
                  onClick={() => navigate('/college/parent-observations')}
                  className="text-amber-900 font-medium text-sm hover:underline"
                >
                  Complete Parent A Observations →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="space-y-8">
        {/* Group questions by section */}
        {['Academic & Learning Profile', 'System Interactions & Edge Cases', 'Natural Tendencies & Uniqueness', 'Parental Perspectives'].map((sectionName, sectionIndex) => {
          const sectionQuestions = parentBQuestions.filter(q => q.section === sectionName);
          const sectionCompleted = sectionQuestions.filter(q => responses[q.id]?.trim()).length;
          
          return (
            <div key={sectionName} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-slate-900">{sectionName}</h3>
                  <div className="text-sm text-slate-600">
                    {sectionCompleted} of {sectionQuestions.length} completed
                  </div>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-slate-900 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(sectionCompleted / sectionQuestions.length) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="space-y-6">
                {sectionQuestions.map((question, questionIndex) => (
                  <div key={question.id} className="border-l-4 border-slate-200 pl-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 ${
                        responses[question.id]?.trim() ? 'bg-emerald-500' : 'bg-slate-300'
                      }`}>
                        {responses[question.id]?.trim() ? <CheckCircle className="w-3 h-3" /> : questionIndex + 1}
                      </div>
                      
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {question.question}
                        </label>
                        
                        <textarea
                          value={responses[question.id] || ''}
                          onChange={(e) => updateField(question.id, e.target.value)}
                          placeholder={question.placeholder}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-200">
        <div className="flex items-center gap-3">
          {saveStatus === 'saving' && (
            <div className="flex items-center gap-2 text-slate-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600"></div>
              <span>Saving...</span>
            </div>
          )}
          {saveStatus === 'saved' && saveMessage && (
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle className="w-4 h-4" />
              <span>{saveMessage}</span>
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="text-red-600">{saveMessage}</div>
          )}
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saveStatus === 'saving'}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium ${
              saveStatus === 'unsaved' 
                ? 'bg-slate-900 text-white hover:bg-slate-800' 
                : saveStatus === 'saving'
                ? 'bg-slate-400 text-white cursor-not-allowed'
                : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            }`}
          >
            <Save className="w-4 h-4" />
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'unsaved' ? 'Save Changes' : 'Saved'}
          </button>
          
          <button
            onClick={() => navigate('/college/student-context-generator')}
            className="flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium"
          >
            Continue to Context Generation
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParentObservationsB;